import {Button, Modal, TextInput} from '@mantine/core';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import React, {useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import type {IntegrationTokenCreateResponse} from 'src/api/ProactApiClient';
import {useCreateIntegrationTokenMutation} from 'src/redux/queries/integrationTokens';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface CreateIntegrationTokenModalProps {
  opened: boolean;
  instanceId: number;
  onClose: () => void;
  onCreated: (token: IntegrationTokenCreateResponse) => void;
}

interface CreateIntegrationTokenFormValues {
  description: string;
}

const emptyValues: CreateIntegrationTokenFormValues = {description: ''};

export const CreateIntegrationTokenModal: React.FC<
  CreateIntegrationTokenModalProps
> = ({opened, instanceId, onClose, onCreated}) => {
  const {setAlert} = useAlert();

  const {control, handleSubmit, reset} =
    useForm<CreateIntegrationTokenFormValues>({
      defaultValues: emptyValues,
    });

  const [createIntegrationToken, {isLoading: isCreating}] =
    useCreateIntegrationTokenMutation();

  useEffect(() => {
    if (opened) {
      reset(emptyValues);
    }
  }, [opened, reset]);

  const handleClose = () => {
    reset(emptyValues);
    onClose();
  };

  const onSubmit = async (values: CreateIntegrationTokenFormValues) => {
    try {
      const result = await createIntegrationToken({
        instanceId,
        description: values.description || undefined,
      }).unwrap();
      reset(emptyValues);
      onCreated(result);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="New Integration Token"
      size="md"
      styles={{title: {fontWeight: 700}}}>
      <Stack gap="md">
        <Controller
          name="description"
          control={control}
          render={({field}) => (
            <TextInput
              label="Description"
              description="Optional label to identify this token"
              placeholder="e.g. Okta Production, Firetrol"
              {...field}
            />
          )}
        />
        <Divider />
        <Group justify="flex-end">
          <Button
            size="sm"
            variant="subtle"
            color="black"
            onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="filled"
            color="yellow"
            onClick={handleSubmit(onSubmit)}
            loading={isCreating}>
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
